import { MomentData, LinkedMoment, Milestone } from '../types';

/**
 * Helper to get nested value from object path (e.g., 'metaDetails.name')
 */
export const getCellValue = (
  moment: MomentData | LinkedMoment,
  field: string
) => {
  const keys = field.split('.');
  let value: any = moment;
  for (const key of keys) {
    value = value?.[key];
  }
  if (Array.isArray(value)) {
    return value.join(', ');
  }
  return value || '';
};

/**
 * Get milestone by name from milestones array
 */
export const getMilestoneByName = (
  milestones: Milestone[],
  name: string
): Milestone | null => {
  return milestones.find(m => m.name === name) || null;
};

/**
 * Format date string supporting multiple formats
 * Handles: ISO dates (YYYY-MM-DD), Quarters (Q1 2025), Months (Sep 2025)
 * @param dateValue - Date string in any supported format
 * @returns Formatted date string or original if format not recognized
 *
 * @example
 * formatPhaseDateShort('2025-02-02') // Returns "Feb 2"
 * formatPhaseDateShort('2025-12-15') // Returns "Dec 15"
 * formatPhaseDateShort('Q1 2026') // Returns "Q1" (quarter only, no year)
 * formatPhaseDateShort('Sep 2025') // Returns "Sep" (month only, no year)
 * formatPhaseDateShort('TBD') // Returns "TBD"
 * formatPhaseDateShort('N/A') // Returns "N/A"
 *
 * Note: Month and quarter show without year for milestone grid column (compact display)
 */
export const formatPhaseDateShort = (dateValue: string): string => {
  if (
    !dateValue ||
    dateValue === '' ||
    dateValue === 'N/A' ||
    dateValue === 'TBD'
  ) {
    return dateValue || '';
  }

  const trimmedDate = dateValue.trim();

  // Handle quarter format (Q1 2025, Q2 2027) - show quarter only, no year
  if (/^Q[1-4]\s+\d{4}$/i.test(trimmedDate)) {
    const quarterMatch = trimmedDate.match(/^(Q[1-4])/i);
    return quarterMatch ? quarterMatch[1].toUpperCase() : trimmedDate;
  }

  // Handle month format (Sep 2025, Mar 2027, September 2025) - show month only, no year
  if (/^[A-Za-z]{3,}\s+\d{4}$/.test(trimmedDate)) {
    const parts = trimmedDate.split(' ');
    const monthStr = parts[0];
    const monthAbbr = monthStr.substring(0, 3);
    const formattedMonth =
      monthAbbr.charAt(0).toUpperCase() + monthAbbr.slice(1).toLowerCase();
    return formattedMonth;
  }

  // Handle ISO date format (YYYY-MM-DD) - format as "Jan 15" (mixed case month + day, no year, no leading zero)
  // IMPORTANT: Parse ISO date WITHOUT timezone conversion to avoid date shifts
  // MATCH FORMAT: This matches the slideout milestone table format (e.g., "Oct 15", "Dec 2")
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmedDate)) {
    const [year, month, day] = trimmedDate.split('-');
    const monthNum = parseInt(month, 10);
    const monthNames = [
      'Jan',
      'Feb',
      'Mar',
      'Apr',
      'May',
      'Jun',
      'Jul',
      'Aug',
      'Sep',
      'Oct',
      'Nov',
      'Dec'
    ];
    const monthName = monthNames[monthNum - 1];
    // Remove leading zero from day (e.g., "02" becomes "2", "15" stays "15")
    const dayNum = parseInt(day, 10);
    return `${monthName} ${dayNum}`;
  }

  // Return original if no pattern matches
  return dateValue;
};

/**
 * Get abbreviated phase name
 * @param phaseName - Full phase name (e.g., "Private Beta", "Public Beta", etc.)
 * @returns Abbreviated phase name (e.g., "PB", "B", "IB", "G")
 */
export const getPhaseAbbreviation = (phaseName?: string): string => {
  if (!phaseName) return '';

  if (phaseName === 'Private Beta') return 'PB';
  if (phaseName === 'Public Beta') return 'B';
  if (phaseName === 'Internal Beta') return '';
  if (phaseName === 'GA') return '';

  return '';
};
